import { createEffect, createSignal, on, Show, useContext } from 'solid-js';
import { Text } from '../../ui-components/text/text';
import { Button } from '../../ui-components/button/button';
import { CoursePath, PathCategory } from '../course-path/course-path';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { gql } from 'graphql-request';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Heading } from '../../ui-components/heading/heading';
import { ShowpadCodeSubmission } from '../showpad-code-submission/showpad-code-submission';
import { StyledLoadingContainer, StyledNoCoursesSection, StyledNoCoursesSectionInner, StyledHeadingContainer } from './course-list-style';
import { Link } from '../../ui-components/link/link';
import { ErrorCatcher } from '../../tools/error-catcher';
import { User } from '../../types/shared';

const GET_PATHS_QUERY = gql`
    query GetPathsAndItemsByAccess {
        getPathsAndItemsByAccess {
            categoryName,
            paths {
                id
                name
                curriculums {
                    id
                    name
                    courses {
                        courseId
                        name
                        description
                        progress
                        completedDate
                        coverImageUrl {
                            small
                        }
                        durationInSeconds
                        tags {
                            name
                        }
                        path {
                            id
                            name
                        }
                        curriculum {
                            id
                            name
                        }
                    }
                }
            }
        }
    }
`;

export const CourseList = () => {
    const {
        userState,
        localize,
        createCachedResource,
        contactUsPage,
        appStore,
    } = useContext(AppContext);
    
    const [pathsCategories, setPathsCategories] = createSignal<PathCategory[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [ pathCategoriesData ] = createCachedResource(GET_PATHS_QUERY, () => ({asdf: userState.user?.showpadCode}), true);

    const hasNoCode = () => userState.user && !userState.user.showpadCode;

    createEffect(
        on(
            () => userState.user,
            (newUser: User | null, prevUser: User | null | undefined) => {
                if (!prevUser && newUser?.uuid) {
                    setLoading(true);
                }
            },
            { defer: true }
        )
    );

    createEffect(() => {
        if (pathCategoriesData() && pathCategoriesData().getPathsAndItemsByAccess) {
            const pathsDataToSet = pathCategoriesData().getPathsAndItemsByAccess;
            setPathsCategories(pathsDataToSet);

            setLoading(false);
        }
    });

    const SubmitCodeSection = () => (
        <StyledNoCoursesSection>
            <Heading tag='h3' variant='bigRed'>
                {localize(
                    'no-courses-available',
                    'No courses available'
                )}
            </Heading>

            <StyledNoCoursesSectionInner>
                <div>
                    <Text fontSize='normal'>
                        {localize(
                            'provide-valid-access-code',
                            'You need a valid access code to get access to the courses. If you do not have one, please contact us.'
                        )}
                    </Text>

                    <Link label={contactUsPage.title} url={contactUsPage.url} />
                </div>

                <ShowpadCodeSubmission />
            </StyledNoCoursesSectionInner>

        </StyledNoCoursesSection>
    );

    const HasCodeButNoCourses = () => (
        <Show when={(userState?.user?.showpadCode) && (!pathsCategories() || pathsCategories()?.length === 0)}>
            <SubmitCodeSection />
        </Show>
    );

    const HasCourses = () => (
        <Show when={pathsCategories()?.length > 0}>
            <CoursePath pathsCategories={pathsCategories()} />
        </Show>
    );

    return (
        <ErrorCatcher componentName='CourseList' logErrorToServer={true}>
            <StyledLoadingContainer>
                <Show when={loading()}>
                    <LoadingSpinner />
                </Show>
            </StyledLoadingContainer>

            <Show when={!loading()}>
                <Show when={userState.user}>
                    <HasCodeButNoCourses />
                    <HasCourses />
                </Show>

                <Show when={!userState.user}>
                    <StyledHeadingContainer>
                        <Heading tag='h3' variant='large' noBlockSpacing={true}>
                            {localize('login-to-view-courses', 'Please login to access our courses')}
                        </Heading>
                    </StyledHeadingContainer>

                    <Text fontSize="normal" displayRedVerticalLine={false}>
                        {localize('login-to-view-courses-paragraph', 'Access personalized training, track your progress, and earn certificates.')}
                    </Text>
                    <Button label={localize('login', 'Login')} variant='primary' onClick={() => appStore.openLoginMenu()} />
                </Show>


                <Show when={hasNoCode()}>
                    <SubmitCodeSection />
                </Show>
            </Show>

        </ErrorCatcher>
    );
};

